/**
 * Formats a date string to a human-readable format.
 * @param dateString - The date string to format
 * @returns The formatted date string
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Format: DD.MM.YYYY HH:MM
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Formats a Date object to dd.MM.yyyy
 * @param date - The Date object to format
 * @returns The formatted date string in dd.MM.yyyy format
 */
export const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Combines date and time strings into ISO format for API.
 * @param date - The date string in dd.MM.yyyy format
 * @param time - The time string in HH:MM format
 * @returns The combined date-time string in YYYY-MM-DDTHH:MM format
 */
export const dateTimeIso = (date: string, time: string): string => {
  // Parse date from dd.MM.yyyy format
  const [day, month, year] = date.split('.');
  // Parse time from HH:MM format
  const [hours, minutes] = time.split(':');

  // Create ISO string (YYYY-MM-DDTHH:MM)
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats time in HH:MM format with leading zeros.
 * @param hours - The hours value
 * @param minutes - The minutes value
 * @returns The formatted time string in HH:MM format
 */
export const formatTime = (hours: number, minutes: number): string => {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Parses an ISO date string into a date string in dd.MM.yyyy format.
 * @param isoDateString - The ISO date string to parse
 * @returns The formatted date string in dd.MM.yyyy format
 */
export const parseDateFromIso = (isoDateString: string): string => {
  if (!isoDateString) return '';

  try {
    const date = new Date(isoDateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return formatDate(date);
  } catch (error) {
    console.error('Error parsing ISO date to date string:', error);
    return '';
  }
};

/**
 * Parses an ISO date string into a time string in HH:MM format.
 * @param isoDateString - The ISO date string to parse
 * @returns The formatted time string in HH:MM format
 */
export const parseTimeFromIso = (isoDateString: string): string => {
  if (!isoDateString) return '';

  try {
    const date = new Date(isoDateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return formatTime(date.getHours(), date.getMinutes());
  } catch (error) {
    console.error('Error parsing ISO date to time string:', error);
    return '';
  }
};

/**
 * Parses an ISO date string into a date-time string in DD.MM.YYYY HH:MM format.
 * @param isoDateString - The ISO date string to parse
 * @returns The formatted date-time string in DD.MM.YYYY HH:MM format
 */
export const parseDateTimeFromIso = (isoDateString: string): string => {
  if (!isoDateString) return '';

  try {
    const date = new Date(isoDateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Format: DD.MM.YYYY HH:MM
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error parsing ISO date to date-time string:', error);
    return '';
  }
};

/**
 * Parses an ISO date string into a date-time string in HH:mm:ss dd.MM.YYYY format.
 * @param isoDateString - The ISO date string to parse
 * @returns The formatted date-time string in HH:mm:ss dd.MM.YYYY format
 */
export const formatDateTimeWithSeconds = (isoDateString: string): string => {
  if (!isoDateString) return '';

  try {
    const date = new Date(isoDateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Format: HH:mm:ss dd.MM.YYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds} ${day}.${month}.${year}`;
  } catch (error) {
    console.error('Error parsing ISO date to date-time string with seconds:', error);
    return '';
  }
};
